import { useState, useEffect, useCallback, useRef } from "react";
import { clubNetworkService } from "../../services/clubNetworkApi";
import { useDebounce } from "../useDebounce";

// Club-facing Agent-Requests feed state. Verified-first ordering comes from the
// backend (Discovery §1) — this hook never re-sorts. Mirrors useAgentTenderFeed
// so the club feed feels identical to the agent feed. Filter set = the backend's
// supported club filters: position / country / type_of_tender (+ search,
// verified-only). NOTE: the backend feed has NO transfer_window filter (window is
// read-only in the payload), so the design's Window filter is intentionally
// omitted rather than faked client-side (per "keine Pflaster").
const DEFAULT_FILTERS = {
  position: "",
  country: "",
  type_of_tender: "",
  // Recruiting Level (professional/youth) — the SAME recruiting_for param the
  // agent feed uses (Part B). Distinct from any league/division filter. Empty =
  // all levels. Shared server-side handling: build_agent_feed_queryset applies
  // apply_recruiting_for identically for the agent feed and this club N2 feed.
  recruiting_for: "",
  verified: false,
};

const ACTIVE_FILTER_KEYS = ["position", "country", "type_of_tender", "recruiting_for", "verified"];

export const useClubNetworkFeed = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sort, setSort] = useState("newest"); // newest | oldest | window_soonest (C13a)
  const [category, setCategory] = useState(""); // "" | players | coaches | staffs | others (C13/P1)
  const [tabCounts, setTabCounts] = useState(null); // {players,coaches,staffs,others}
  const [savedOnly, setSavedOnly] = useState(false); // "Saved" view (C12, ?saved=true)
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  });

  const abortRef = useRef(null);

  const buildParams = useCallback(
    (page) => {
      const p = new URLSearchParams();
      p.append("page", page);
      ["position", "country", "type_of_tender", "recruiting_for"].forEach((k) => {
        if (filters[k]) p.append(k, filters[k]);
      });
      if (filters.verified) p.append("verified", "true");
      if (debouncedSearch) p.append("search", debouncedSearch);
      p.append("sort_by", sort); // BE: "oldest"/"created_asc" → oldest-first, else newest
      if (category) p.append("category", category);
      if (savedOnly) p.append("saved", "true");
      return p;
    },
    [filters, debouncedSearch, sort, category, savedOnly],
  );

  const fetchRequests = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await clubNetworkService.getRequests(
          buildParams(page),
          controller.signal,
        );
        if (res.success) {
          setRequests(res.data);
          setPagination(res.pagination);
          setTabCounts(res.tabCounts || null);
          // you_contacted is NOT on feed items (D-003) — enrich the visible page
          // via the batch contacted/ endpoint (agent-granular, no N+1).
          const ids = res.data.map((r) => r.id).filter(Boolean);
          if (ids.length) {
            const c = await clubNetworkService.getContacted(ids, controller.signal);
            if (c.success) {
              const set = new Set((c.data || []).map((x) => Number(x)));
              const attr = c.attribution || {};
              // Merge club-wide contacted marker + attribution (DoD 27): who on
              // the club reached out, and when — graceful when only the flag exists.
              setRequests((prev) =>
                prev.map((r) => {
                  const a = attr[r.id] || attr[String(r.id)] || null;
                  return {
                    ...r,
                    you_contacted: set.has(Number(r.id)),
                    contacted_by: a?.contacted_by || null,
                    contacted_at: a?.contacted_at || null,
                  };
                }),
              );
            }
            // Saved-state batch (C12) — the feed list omits is_saved inline, so
            // the card status comes from this side channel.
            const s = await clubNetworkService.getSaved(ids, controller.signal);
            if (s.success) {
              const savedSet = new Set((s.data || []).map((x) => Number(x)));
              setRequests((prev) =>
                prev.map((r) => ({ ...r, is_saved: savedSet.has(Number(r.id)) })),
              );
            }
          }
        } else {
          setError(res.error || "Failed to load agent requests.");
        }
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setError("Failed to load agent requests. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [buildParams],
  );

  useEffect(() => {
    fetchRequests(1);
  }, [fetchRequests]);

  const applyFilters = (next) => {
    setFilters(next);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const changeSort = (s) => {
    const allowed = ["newest", "oldest", "window_soonest"];
    setSort(allowed.includes(s) ? s : "newest");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const toggleSavedOnly = () => {
    setSavedOnly((v) => !v);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Category pill (C13/P1). Clicking the active pill clears back to "all".
  const changeCategory = (c) => {
    setCategory((prev) => (prev === c ? "" : c));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Optimistic save/unsave toggle (C12). The endpoints aren't idempotent, so we
  // branch on the known is_saved state; on failure we revert the optimistic flip.
  const toggleSaved = useCallback(async (request) => {
    const wasSaved = !!request.is_saved;
    setRequests((prev) =>
      prev.map((r) => (r.id === request.id ? { ...r, is_saved: !wasSaved } : r)),
    );
    const res = wasSaved
      ? await clubNetworkService.unsaveRequest(request.id)
      : await clubNetworkService.saveRequest(request.id);
    if (!res.success) {
      setRequests((prev) =>
        prev.map((r) => (r.id === request.id ? { ...r, is_saved: wasSaved } : r)),
      );
    }
  }, []);

  const toggleVerifiedOnly = () =>
    applyFilters({ ...filters, verified: !filters.verified });

  const removeFilter = (key) =>
    applyFilters({ ...filters, [key]: key === "verified" ? false : "" });

  const hasActiveFilters = ACTIVE_FILTER_KEYS.some((k) => !!filters[k]);

  const clearFilters = () => applyFilters(DEFAULT_FILTERS);

  return {
    requests,
    loading,
    error,
    showFilterModal,
    setShowFilterModal,
    filters,
    applyFilters,
    toggleVerifiedOnly,
    removeFilter,
    hasActiveFilters,
    clearFilters,
    searchTerm,
    setSearchTerm,
    pagination,
    fetchRequests,
    sort,
    changeSort,
    category,
    tabCounts,
    changeCategory,
    savedOnly,
    toggleSavedOnly,
    toggleSaved,
  };
};
